import type { IncomingMessage } from 'http';
import type { NextParsedUrlQuery } from 'next/dist/server/request-meta';
import UAParser from 'ua-parser-js';

import type { DeviceType, OSType } from '@/types';
import { isSSR } from './isSSR';

export const isMobile = (): boolean => {
  if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) {
    return true;
  }
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

export const isInApp = (query?: { [key: string]: any }): boolean => {
  if (isSSR) {
    if (query != null) {
      return query.os_type != null || query.app != null;
    }
    return false;
  }

  let triggered = false;
  try {
    triggered = sessionStorage.webviewTriggered === 'yes';
  } catch {
    //
  }

  const search = new URLSearchParams(location.search);
  const osTypeQuery = search.get('os_type');

  if (
    triggered ||
    (osTypeQuery != null && osTypeQuery !== '') ||
    search.get('app') != null
  ) {
    try {
      sessionStorage.webviewTriggered = 'yes';
    } catch {
      //
    }
    return true;
  }
  return false;
};

export const getOSType = (query: NextParsedUrlQuery): OSType => {
  if (isSSR) {
    return 'WEB';
  }

  const osType = query?.os_type as string;
  const osTypeParams = osType?.toUpperCase() as OSType;

  const userAgent = navigator?.userAgent;
  const parser = new UAParser(userAgent);
  const result = parser.getResult();

  const isApp = osTypeParams && ['ANDROID', 'IOS'].includes(osTypeParams);

  if (isApp) {
    return osTypeParams;
  } else if (result.device.type) {
    return result.os.name === 'iOS' ? 'MWEBIOS' : 'MWEBANDROID';
  } else {
    return 'WEB';
  }
};

export const getOSTypeSSR = (
  req?: IncomingMessage & {
    _parsedUrl?: {
      query: string[][] | Record<string, string> | string | URLSearchParams;
    };
  },
): OSType => {
  const params = new URLSearchParams(req?._parsedUrl?.query);
  const osTypeParams = params.get('os_type')?.toUpperCase() as OSType;
  const userAgent = req?.headers['user-agent'];
  const parser = new UAParser(userAgent);
  const result = parser.getResult();

  const isApp = osTypeParams && ['ANDROID', 'IOS'].includes(osTypeParams);

  if (isApp) {
    return osTypeParams;
  } else if (result.device.type) {
    return result.os.name === 'iOS' ? 'MWEBIOS' : 'MWEBANDROID';
  } else {
    return 'WEB';
  }
};

export const getDeviceType = (osType: OSType): DeviceType => {
  switch (osType) {
    case 'IOS':
    case 'ANDROID':
      return 'mobile';
    case 'MWEBANDROID':
    case 'MWEBIOS':
      return 'mobileWeb';
    case 'WEB':
    default:
      return 'web';
  }
};
